import { useState, useRef, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ASSETS_URL = "";

// Background Music Player Component
const MusicPlayer = ({ autoPlay = false }) => {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);

  useEffect(() => {
    if (autoPlay && audioRef.current) {
      // Try to auto-play when component mounts
      const playPromise = audioRef.current.play();
      if (playPromise !== undefined) {
        playPromise.then(() => {
          setIsPlaying(true);
        }).catch(() => {
          // Auto-play was blocked, user needs to click
          setIsPlaying(false);
        });
      }
    }
  }, [autoPlay]);

  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  return (
    <div className="fixed bottom-6 right-6 z-50" data-testid="music-player">
      <audio ref={audioRef} src="/background-music.mp3" loop />
      <button
        onClick={togglePlay}
        className="bg-red-600 hover:bg-red-500 text-white w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-all transform hover:scale-110"
        data-testid="music-toggle-btn"
        aria-label={isPlaying ? "Pause background music" : "Play background music"}
      >
        {isPlaying ? (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        ) : (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
          </svg>
        )}
      </button>
    </div>
  );
};

// PayPal Donate Button Component
const PayPalDonateButton = () => {
  useEffect(() => {
    // Initialize PayPal button after component mounts
    if (window.paypal && window.paypal.HostedButtons) {
      window.paypal.HostedButtons({
        hostedButtonId: "GBCZX2T5TXZMU",
      }).render("#paypal-container-GBCZX2T5TXZMU");
    }
  }, []);

  return (
    <div id="paypal-container-GBCZX2T5TXZMU" className="mt-6" data-testid="paypal-donate-button"></div>
  );
};

// Magic Fireball Energy Animation Component
const MagicFireball = () => {
  return (
    <div className="magic-fireball-container" data-testid="magic-fireball">
      <div className="magic-fireball"></div>
      <div className="energy-wisp"></div>
      <div className="energy-wisp energy-wisp-2"></div>
    </div>
  );
};

// Pulsating Rings Animation Component
const PulsatingRings = () => {
  return (
    <div className="pulsating-rings-container" data-testid="pulsating-rings">
      {/* Purple rings - top */}
      <div className="ring-group-purple">
        <div className="ring-purple"></div>
        <div className="ring-purple"></div>
        <div className="ring-purple"></div>
        <div className="ring-purple"></div>
        <div className="ring-purple"></div>
      </div>
      {/* White rings - middle */}
      <div className="ring-group-white">
        <div className="ring-white"></div>
        <div className="ring-white"></div>
        <div className="ring-white"></div>
        <div className="ring-white"></div>
      </div>
      {/* Red rings - bottom */}
      <div className="ring-group-red">
        <div className="ring-red"></div>
        <div className="ring-red"></div>
        <div className="ring-red"></div>
        <div className="ring-red"></div>
        <div className="ring-red"></div>
      </div>
    </div>
  );
};

// Navigation Component
const Navigation = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  return (
    <nav className="bg-black border-b border-gray-800 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-20 items-center">
          <Link to="/" className="flex-shrink-0">
            <img src="/logo.png" alt="Rocky Mountain GPS Logo" className="h-16 w-auto border-2 border-red-600 rounded" />
          </Link>
          <div className="hidden lg:flex items-center space-x-5">
            <Link to="/" className="text-white hover:text-white transition text-base font-medium">Home</Link>
            <Link to="/about" className="text-white hover:text-white transition text-base font-medium">About</Link>
            <Link to="/programs" className="text-white hover:text-white transition text-base font-medium">Programs</Link>
            <Link to="/facilities" className="text-white hover:text-white transition text-base font-medium">Facilities</Link>
            <Link to="/quotes" className="text-white hover:text-white transition text-base font-medium">Testimonials</Link>
            <Link to="/get-involved" className="text-white hover:text-white transition text-base font-medium">Get Involved</Link>
            <Link to="/news" className="text-white hover:text-white transition text-base font-medium">News</Link>
            <Link to="/revenue" className="text-white hover:text-white transition text-base font-medium">Economic Impact</Link>
            <Link to="/contact" className="text-white hover:text-white transition text-base font-medium">Contact</Link>
            <Link to="/leadership" className="text-white hover:text-white transition text-base font-medium">Leadership</Link>
            <Link to="/investment" className="text-white hover:text-white transition text-base font-medium">Investment</Link>
            <Link to="/imagine" className="text-white hover:text-white transition text-base font-medium">Imagine</Link>
            <Link to="/get-involved#donate" className="bg-red-600 text-white px-5 py-2 rounded-full font-semibold hover:bg-red-500 transition text-base whitespace-nowrap">Donate Now</Link>
          </div>
          <button 
            className="lg:hidden text-white"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </div>
        {mobileMenuOpen && (
          <div className="lg:hidden pb-4 space-y-2">
            <Link to="/" className="block text-white hover:text-white py-2">Home</Link>
            <Link to="/about" className="block text-white hover:text-white py-2">About</Link>
            <Link to="/programs" className="block text-white hover:text-white py-2">Programs</Link>
            <Link to="/facilities" className="block text-white hover:text-white py-2">Facilities</Link>
            <Link to="/quotes" className="block text-white hover:text-white py-2">Testimonials</Link>
            <Link to="/get-involved" className="block text-white hover:text-white py-2">Get Involved</Link>
            <Link to="/news" className="block text-white hover:text-white py-2">News</Link>
            <Link to="/revenue" className="block text-white hover:text-white py-2">Economic Impact</Link>
            <Link to="/contact" className="block text-white hover:text-white py-2">Contact</Link>
            <Link to="/leadership" className="block text-white hover:text-white py-2">Leadership</Link>
            <Link to="/investment" className="block text-white hover:text-white py-2">Investment</Link>
            <Link to="/imagine" className="block text-white hover:text-white py-2">Imagine</Link>
            <Link to="/get-involved#donate" className="block bg-red-600 text-white px-5 py-2 rounded-full font-semibold text-center mt-2">Donate Now</Link>
          </div>
        )}
      </div>
    </nav>
  );
};

// Footer Component
const Footer = () => (
  <footer className="bg-black border-t border-gray-800">
    {/* Top Footer */}
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
        {/* Logo and Tagline */}
        <div>
          <Link to="/" className="flex items-center gap-2 mb-3">
            <img src="/logo.png" alt="Rocky Mountain GPS Logo" className="h-10 w-auto border-2 border-red-600 rounded" />
            <span className="text-red-600 font-bold text-base italic">Rocky Mountain GPS</span>
          </Link>
          <p className="text-white italic text-sm">A Better Way to Build - A Better Way to Grow</p>
        </div>
        
        {/* Quick Links */}
        <div>
          <h3 className="text-white font-bold text-sm mb-4">Quick Links</h3>
          <div className="space-y-2">
            <Link to="/programs" className="block text-white hover:text-red-500 transition text-sm">Programs</Link>
            <Link to="/get-involved" className="block text-white hover:text-red-500 transition text-sm">Get Involved</Link>
            <Link to="/news" className="block text-white hover:text-red-500 transition text-sm">News</Link>
          </div>
        </div>
        
        {/* Contact Information */}
        <div className="md:col-span-2">
          <h3 className="text-red-600 font-bold text-base italic mb-4">Contact Information</h3>
          <div className="grid grid-cols-3 gap-4 text-white text-sm">
            <div>
              <p className="font-semibold mb-1">Address</p>
              <p>854 N 50 W Alfalfa Circle</p>
              <p>Midway, Utah 84049</p>
            </div>
            <div>
              <p className="font-semibold mb-1">Email</p>
              <a href="mailto:info@rockymountaingps.org" className="hover:text-red-500 transition">info@rockymountaingps.org</a>
            </div>
            <div>
              <p className="font-semibold mb-1">Phone</p>
              <a href="tel:435.709.6445" className="hover:text-red-500 transition">435.709.6445</a>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    {/* Bottom Footer */}
    <div className="border-t border-gray-800 py-8">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-4">
        <p className="text-white text-sm">© 2026 <span className="text-red-600 italic">Rocky Mountain Global Peace & Sports Complex</span>. All rights reserved.</p>
        <p className="text-gray-400 text-xs leading-relaxed max-w-3xl mx-auto">
          <span className="text-red-600 italic">Rocky Mountain Global Peace & Sports Complex (Rocky Mountain GPS)</span> is a registered nonprofit in the State of Utah, doing business under the auspices of <em>thunder</em>Heart, Inc. A 501(c)(3) non-for-profit that has been registered with the Internal Revenue Service (IRS) since 2001.
        </p>
        <a href="https://rockymountaingps.org" target="_blank" rel="noopener noreferrer" className="inline-block text-red-600 hover:text-red-500 transition text-sm font-semibold">ROCKYMOUNTAINGPS.ORG</a>
      </div>
    </div>
  </footer>
);

// Facility Card Component
const FacilityCard = ({ title, desc, image, largeTitle, italicNote }) => (
  <div className="bg-black border border-gray-700 p-6 rounded-lg shadow-md hover:shadow-xl hover:border-gray-500 transition-shadow">
    <h3 className={`font-bold text-red-600 mb-2 ${largeTitle ? 'text-2xl' : 'text-xl'}`}>{title}</h3>
    <p className="text-white">{desc}</p>
    {italicNote && <p className="text-white italic mt-2">{italicNote}</p>}
    {image && (
      <img src={image} alt={title} className="mt-4 w-full rounded-lg" />
    )}
  </div>
);

// Home Page
const HomePage = () => {
  return (
    <div className="home-page bg-black text-white" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative bg-black text-white py-24" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-6xl md:text-7xl font-bold text-red-600 mb-6">
              <span className="block italic">Rocky Mountain Global Peace</span>
              <span className="block text-red-600 italic">& Sports Complex</span>
            </h1>
            
            {/* Spinning Globe */}
            <div className="flex justify-center mb-8">
              <video 
                autoPlay 
                loop 
                muted 
                playsInline
                poster="/globe-poster.png"
                className="w-80 h-80"
                data-testid="home-globe-video"
              >
                <source src="/orange-spinning-globe.mp4" type="video/mp4" />
              </video>
            </div>
            
            <p className="text-xl md:text-2xl text-white max-w-4xl mx-auto mb-8 leading-relaxed" data-testid="hero-description">
              Our goal is to create a place of refuge where children develop compassion through sports, teamwork, and creativity.
            </p>
            <p className="text-lg text-white max-w-3xl mx-auto mb-8">
              Here, they can discover the importance of science, environmental stewardship, and teamwork—taking home skills to shape their futures and impact the world.
            </p>
            <div className="mb-10">
              <img 
                src="/sprung-night.png"
                alt="Rocky Mountain Global Peace & Sports Complex Facility Rendering"
                className="mx-auto rounded-lg shadow-2xl max-w-4xl w-full"
                data-testid="facility-rendering"
              />
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/get-involved#donate"
                className="inline-block bg-white border-2 border-blue-800 text-blue-700 px-8 py-4 rounded-full text-lg font-bold hover:bg-blue-900 hover:text-white transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                data-testid="hero-donate-button"
              >
                Support Our Mission
              </Link>
              <Link 
                to="/about"
                className="inline-block bg-transparent border-2 border-white text-white px-8 py-4 rounded-full text-lg font-bold hover:bg-white hover:text-black transition-all shadow-lg"
                data-testid="hero-learn-more-button"
              >
                Learn More
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* The Reasons Why Section */}
      <section className="py-16 bg-black" data-testid="reasons-why-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center text-red-600 mb-12">THE REASONS WHY</h2>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Left Column - 1, 2, 3 */}
            <div className="space-y-8">
              {/* 1. The Vision */}
              <div className="border-l-4 border-red-600 pl-6">
                <h3 className="text-2xl font-bold text-red-600 mb-3">1. THE VISION</h3>
                <p className="text-white text-lg leading-relaxed">
                  Our Vision is to empower children through play, sports, and comprehensive sports programs designed by mentors from all walks of life. We are here not only to help them be better athletes but to promote positive life outcomes. This is a place where children from wide-ranging economic, social, and political backgrounds learn valuable, life-affirming lessons of sports through training, teamwork, and creativity.
                </p>
              </div>

              {/* 2. The Promise */}
              <div className="border-l-4 border-red-600 pl-6">
                <h3 className="text-2xl font-bold text-red-600 mb-3">2. THE PROMISE</h3>
                <p className="text-white text-2xl italic mb-3">"No child will be turned away for the inability to pay."</p>
                <p className="text-white text-lg leading-relaxed">
                  We are committed to full accessibility. Our scholarship model guarantees unlimited access to our facilities, programs, camps, and events. By removing financial barriers, we ensure that every child has the same opportunity to benefit from world-class coaching, equipment, and mentorship.
                </p>
              </div>

              {/* 3. An Infrastructure Ecosystem */}
              <div className="border-l-4 border-red-600 pl-6">
                <h3 className="text-2xl font-bold text-red-600 mb-3">3. AN INFRASTRUCTURE ECOSYSTEM</h3>
                <p className="text-white text-lg leading-relaxed">
                  This is a once-in-a-generation opportunity to combine Education, Housing, Wellness, and Sports on a single campus. By integrating these four pillars, we support the "whole child." It is a safe environment where athletic training, academic achievement, and personal health happen together, giving kids a consistent foundation to thrive.
                </p>
              </div>
            </div>

            {/* Right Column - 4, 5, Video */}
            <div className="space-y-8">
              {/* 4. A Place of Refuge */}
              <div className="border-l-4 border-red-600 pl-6">
                <h3 className="text-2xl font-bold text-red-600 mb-3">4. A PLACE OF REFUGE</h3>
                <p className="text-white text-lg leading-relaxed">
                  Our facility is a sanctuary for children of all abilities. Whether it is through traditional sports, or nature-based recreation, we provide a Place of Refuge dedicated to healing and peace. We are building a legacy of health and joy that serves a child's character as much as their athletic skills.
                </p>
              </div>

              {/* 5. Our Success Metrics */}
              <div className="border-l-4 border-red-600 pl-6">
                <h3 className="text-2xl font-bold text-red-600 mb-3">5. OUR SUCCESS METRICS</h3>
                <p className="text-white text-xl italic mb-4">"A community is measured by how it invests in its children's future."</p>
                <p className="text-white text-lg mb-4">Success is measured by the lives we impact and the character we build:</p>
                <ul className="text-white text-lg space-y-2 ml-4">
                  <li className="flex items-start"><span className="text-red-600 mr-3">•</span>Scholarship Awards that change family trajectories.</li>
                  <li className="flex items-start"><span className="text-red-600 mr-3">•</span>Participation Growth across all backgrounds.</li>
                  <li className="flex items-start"><span className="text-red-600 mr-3">•</span>Our Mentorship Programs are dedicated to building Compassion, Spirit, and Discipline.</li>
                  <li className="flex items-start"><span className="text-red-600 mr-3">•</span>Wide ranging Metrics that reflect our commitment to every child.</li>
                </ul>
              </div>

              {/* Video */}
              <div className="mt-8">
                <p className="text-2xl font-bold text-red-600 italic mb-4 text-center">WE'VE GOT GREATNESS TO BUILD ... WE HOPE YOU WILL AGREE.</p>
                <video 
                  autoPlay 
                  loop 
                  muted 
                  playsInline
                  controls
                  className="w-full rounded-lg border-2 border-red-600"
                  data-testid="greatness-video"
                >
                  <source src="/greatness-video.mp4" type="video/mp4" />
                </video>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Have More Questions Section */}
      <section className="py-12 bg-gray-900" data-testid="faq-cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-red-600 mb-4">HAVE MORE QUESTIONS?</h2>
          <p className="text-white text-lg mb-6">Learn more about our mission, our safety standards, and our innovative multi-sport facility on our <Link to="/about#faq" className="text-red-500 underline hover:text-red-400">Project FAQ Page</Link>.</p>
        </div>
      </section>

      {/* Languages of Change Section */}
      <section className="py-16 bg-black" data-testid="languages-of-change-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-red-600 mb-6">The Languages of Change</h2>
          <p className="text-xl text-white mb-4">
            At the <strong className="text-red-600 italic">Rocky Mountain Global Peace & Sports Complex</strong>, we believe in the transformative power of two universal languages: <strong>sport and compassion</strong>.
          </p>
          <p className="text-2xl text-white mb-4 font-bold">We don't just believe in change—we build it.</p>
          <p className="text-2xl font-bold text-red-600 italic">We've got greatness to build...we hope you will join us</p>
        </div>
      </section>

      {/* Facilities Section */}
      <section className="py-16 bg-gray-900" data-testid="facilities-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center text-red-600 mb-12">World-Class Facilities</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <FacilityCard title="State-of-the-Art NHL Regulation Sheets" desc={<>The <span className="text-red-600 italic">Rocky Mountain Global Peace & Sports Complex</span> features two and a half precision-engineered NHL-standard ice sheets (85' x 200'). Designed for peak performance, our facility utilizes advanced climate control and surfacing technology to provide a world-class environment for elite athletes and recreational skaters alike. Whether hosting international tournaments or local development, our infrastructure is built to exceed professional standards.</>} image="/hockey-kid-pink.png" />
            {/* Wellness/Training Center - Special Layout */}
            <div className="bg-black border border-gray-700 p-6 rounded-lg shadow-md hover:shadow-xl hover:border-gray-500 transition-shadow">
              <h3 className="text-xl font-bold text-red-600 mb-4">Wellness/Training Center</h3>
              <p className="text-white mb-6">Our Center combines a nurturing environment with elite computational infrastructure. Using next-gen workstations and adaptive training software, we provide an immersive strengthening experience. From high-tech performance analytics to personalized development tracks, we use the world's best tools to build the world's all-around kids.</p>
              
              <h4 className="text-lg font-bold text-red-600 mb-2">The Holistic Athlete Development Ecosystem</h4>
              <p className="text-white mb-6">We provide sport-specific training and recovery through professional-grade physical therapy, state-of-the-art performance testing, and advanced recovery equipment-all available to every program participant. This is a place where the power of mind-body interaction is harnessed for sports performance, injury recovery, and essential life skills.</p>
              
              <h4 className="text-lg font-bold text-red-600 mb-2">Eliminating Barriers to Excellence</h4>
              <p className="text-white mb-4">We are providing excellent clinical care and education to promote lifelong health and fitness habits. As the only facility in the region offering comprehensive training, education, accommodation, and wellness on a single campus, we eliminate transportation barriers and maximize development time for every child.</p>
              
              <img src="/wellness-gym.png" alt="Wellness/Training Center" className="mt-4 w-1/2 mx-auto rounded-lg" />
            </div>
            <FacilityCard title="Equine Therapy" desc="Partnering with local equine rescue center, we will offer Equine-assisted programs that help youth from all backgrounds, including those with Autism, PTSD, and cognitive challenges. Our nature-based approach supports emotional healing and mental wellness, bridging sports and holistic health for every child. Equine centered growth and healing programs combined with authentic and compassionate growth and connection with horses." image="/equestrian-autumn.jpg" largeTitle={true} />
            
            <FacilityCard title="Scenic Trails" desc="The Complex will include an extensive trail system that will connect existing trails to new trails to make the pathways contiguous through the county and eventually connect the UDOT trail systems. Extensive landscaping is part of our master plan. There will be lots and lots of open space, free flowing waterways, green space, and lots of trees." image="/provo-river-sunrise.png" />
            
            {/* Combined Additional Facilities */}
            <div className="bg-black border border-gray-700 p-6 rounded-lg shadow-md">
              <h3 className="text-xl font-bold text-red-600 mb-4">Additional Facilities</h3>
              <ul className="text-white space-y-2">
                <li className="flex items-start"><span className="text-red-600 mr-2">•</span>Basketball Courts - Two professional-grade courts</li>
                <li className="flex items-start"><span className="text-red-600 mr-2">•</span>Charter School - Educational programs integrated with athletics</li>
                <li className="flex items-start"><span className="text-red-600 mr-2">•</span>Dormitories - Two comfortable dormitories for visiting teams</li>
                <li className="flex items-start"><span className="text-red-600 mr-2">•</span>Multi-Sport Field - Baseball, soccer, lacrosse, rugby, and football</li>
              </ul>
            </div>
            
            <FacilityCard title="Heber Valley" desc="Strategically located in the coveted North Highway 40 corridor, this property affords easy access to the Salt Lake International Airport with the luxury of a scenic, traffic-light-free commute. Located in the beautiful Heber Valley, the heart of Utah's Mountain Adventure. Easy access to Park City, Sundance and Salt Lake City. The property offers a stunning panoramic view of the majestic Mount Timpanogos, with walkable access to the world-renowned Provo River." italicNote="This strategic location is critical for our success — bringing the world to Utah's doorstep." image="/open-space-mountains.png" />
          </div>
          
          <div className="text-center mt-10">
            <Link 
              to="/programs"
              className="inline-block bg-white text-black px-8 py-3 rounded-full font-semibold hover:bg-gray-200 transition-colors shadow-md"
              data-testid="view-all-programs-button"
            >
              View All Programs
            </Link>
          </div>
        </div>
      </section>

      {/* Educational Programs Section */}
      <section className="py-16 bg-black" data-testid="educational-programs-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-red-600 mb-4">Educational Programs</h2>
            <p className="text-xl text-white max-w-3xl mx-auto">Educational, residential, and wellness facilities for complete athlete development</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-gray-900 border border-gray-700 rounded-lg overflow-hidden hover:border-gray-500 transition-colors" data-testid="edu-charter-school">
              <div className="p-6">
                <h3 className="text-2xl font-bold text-red-600 mb-3">Charter School</h3>
                <p className="text-white mb-4">Integrated academic programs that combine traditional education with athletic development.</p>
                <ul className="text-white space-y-1 mb-4">
                  <li>• Core Academic Curriculum: STEM Programs, Arts & Music, Character Education</li>
                </ul>
              </div>
              <img src={`${ASSETS_URL}/edu-charter-school.png`} alt="Charter School Campus" className="w-full h-48 object-cover object-center" />
            </div>
            <div className="bg-gray-900 border border-gray-700 rounded-lg overflow-hidden hover:border-gray-500 transition-colors" data-testid="edu-mentorship">
              <div className="p-6">
                <h3 className="text-2xl font-bold text-red-600 mb-3">Mentorship Programs</h3>
                <p className="text-white mb-4">Connecting children with positive role models and mentors from diverse backgrounds.</p>
                <ul className="text-white space-y-1 mb-4">
                  <li>• One-on-One Mentoring</li>
                  <li>• Leadership Development</li>
                  <li>• Life Skills Workshops</li>
                  <li>• Career Exploration</li>
                </ul>
              </div>
              <img src={`${ASSETS_URL}/edu-mentorship.png`} alt="Mentorship Programs" className="w-full h-48 object-cover object-center" />
            </div>
            <div className="bg-gray-900 border border-gray-700 rounded-lg overflow-hidden hover:border-gray-500 transition-colors" data-testid="edu-environmental">
              <div className="p-6">
                <h3 className="text-2xl font-bold text-red-600 mb-3">Environmental Stewardship</h3>
                <p className="text-white mb-4">Outdoor education programs teaching children about nature and environmental responsibility.</p>
                <ul className="text-white space-y-1 mb-4">
                  <li>• Trail Maintenance</li>
                  <li>• Wildlife Education</li>
                  <li>• Sustainable Practices</li>
                  <li>• Outdoor Adventures</li>
                </ul>
              </div>
              <img src={`${ASSETS_URL}/edu-environmental.png`} alt="Environmental Stewardship" className="w-full h-48 object-cover object-center" />
            </div>
            <div className="bg-gray-900 border border-gray-700 rounded-lg overflow-hidden hover:border-gray-500 transition-colors" data-testid="edu-music-arts">
              <div className="p-6">
                <h3 className="text-2xl font-bold text-red-600 mb-3">Music, Arts, High Tech Creativity</h3>
                <p className="text-white mb-4">Creative programs that complement athletic development through music, artistic expression, and cutting-edge technology.</p>
                <ul className="text-white space-y-1 mb-4">
                  <li>• Music Lessons</li>
                  <li>• Band & Orchestra</li>
                  <li>• Art Classes</li>
                  <li>• Performance Opportunities</li>
                </ul>
              </div>
              <img src={`${ASSETS_URL}/edu-music-arts.png`} alt="Music Arts Technology" className="w-full h-48 object-cover object-center" />
            </div>
          </div>
          <div className="mt-8 bg-gray-900 p-6 rounded-lg border border-gray-700">
            <p className="text-white text-center">
              <span className="text-red-600 font-semibold">Holistic Development:</span> The complex is designed for complete athlete development—combining education, housing, and wellness in one location. This integration creates a supportive ecosystem that nurtures not just athletic skills but academic achievement and character growth.
            </p>
          </div>
        </div>
      </section>

      {/* Join Community Section */}
      <section className="py-16 bg-black text-white" data-testid="get-involved-cta-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center mb-12 text-red-600">Join Our Community</h2>
          <div className="mb-12">
            <div className="max-w-4xl mx-auto">
              <video 
                controls 
                className="w-full rounded-lg shadow-2xl" 
                poster={`${ASSETS_URL}/sprung-rendering.png`}
                data-testid="community-video"
              >
                <source src={`${ASSETS_URL}/rmgps-video.mp4`} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-gray-900 p-8 rounded-lg text-center hover:bg-gray-800 transition-colors">
              <h3 className="text-2xl font-bold mb-4 text-red-600">Donate</h3>
              <p className="mb-6">Help us build a brighter future for children through your generous support.</p>
              <Link to="/get-involved#donate" className="inline-block bg-red-600 text-white px-6 py-2 rounded-full font-semibold hover:bg-red-500 transition-colors">Give Now</Link>
            </div>
            <div className="bg-gray-900 p-8 rounded-lg text-center hover:bg-gray-800 transition-colors">
              <h3 className="text-2xl font-bold mb-4 text-red-600">Volunteer</h3>
              <p className="mb-6">Share your time and skills to make a direct impact on children's lives.</p>
              <Link to="/get-involved#volunteer" className="inline-block bg-white border-2 border-blue-800 text-blue-700 px-6 py-2 rounded-full font-semibold hover:bg-blue-900 hover:text-white transition-colors">Get Involved</Link>
            </div>
            <div className="bg-gray-900 p-8 rounded-lg text-center hover:bg-gray-800 transition-colors">
              <h3 className="text-2xl font-bold mb-4 text-red-600">Partner</h3>
              <p className="mb-6">Join us as a corporate or community partner to amplify our impact.</p>
              <Link to="/get-involved#partner" className="inline-block bg-blue-600 text-white px-6 py-2 rounded-full font-semibold hover:bg-blue-500 transition-colors">Become a Partner</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

// About Page
const AboutPage = () => {
  return (
    <div className="about-page bg-black text-white">
      <MusicPlayer />
      <section className="bg-gray-900 text-white py-20" data-testid="about-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold mb-6 text-red-600">About Us</h1>
          <p className="text-xl max-w-3xl mx-auto">Building bridges between communities through the power of sports and education</p>
        </div>
      </section>

      <section className="py-20 bg-black" data-testid="our-story">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold mb-6 text-red-600">Our Story</h2>
              <p className="text-lg text-white mb-4">
                The <span className="text-red-600 italic">Rocky Mountain Global Peace & Sports Complex</span> was founded with a vision to create a sanctuary where children from all backgrounds can come together, learn, and grow through the universal language of sports.
              </p>
              <p className="text-lg text-white mb-4">
                Located in the beautiful Heber Valley, our state-of-the-art facility provides world-class training opportunities while fostering compassion, teamwork, and leadership skills that extend far beyond the playing field.
              </p>
              <p className="text-lg text-white">
                We believe that every child deserves the opportunity to discover their potential and make a positive impact on the world.
              </p>
            </div>
            <div>
              <img src="/sprung-building-hq.png" alt="Rocky Mountain Global Peace and Sports Complex Building" className="w-full h-96 object-cover rounded-lg shadow-xl" />
            </div>
          </div>
        </div>
      </section>

      {/* Project FAQs Section */}
      <section className="py-20 bg-black" data-testid="project-faqs" id="faq">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center mb-12 text-red-600">Project FAQs</h2>
          
          <div className="space-y-8">
            <div className="border-l-4 border-red-600 pl-6">
              <h3 className="text-xl font-bold text-red-600 mb-2">1. What is the scale and scope of this project?</h3>
              <p className="text-white text-lg">We are planning a world-class infrastructure ecosystem that integrates Education, Housing, Wellness, and Sports on a single campus. The facility is designed to feature 2.5 sheets of NHL-sized ice and multi-sport courts with the flexibility to host large-scale community events and elite tournaments.</p>
            </div>

            <div className="border-l-4 border-red-600 pl-6">
              <h3 className="text-xl font-bold text-red-600 mb-2">2. Who is the facility being built for?</h3>
              <p className="text-white text-lg">The complex is for children of all abilities and backgrounds. Our core mission is "no child will be turned away for the inability to pay," ensuring that world-class mentorship and facilities are accessible to everyone, regardless of economic status.</p>
            </div>

            <div className="border-l-4 border-red-600 pl-6">
              <h3 className="text-xl font-bold text-red-600 mb-2">3. Why are you using Sprung Structures for the facility?</h3>
              <p className="text-white text-lg mb-3">We have selected Sprung Structures because they are the gold standard for high-performance sports environments.</p>
              <ul className="text-white text-lg space-y-2 ml-4">
                <li><span className="text-red-500 font-bold">Speed to Mission:</span> This innovative technology allows us to move from groundbreaking to "ice-on" significantly faster than traditional construction, getting kids into programs sooner.</li>
                <li><span className="text-red-500 font-bold">Superior Performance:</span> These structures provide exceptional insulation and climate control, ensuring world-class ice quality and a comfortable environment year-round.</li>
                <li><span className="text-red-500 font-bold">Bright & Airy:</span> The design allows for soaring ceilings and natural light, creating a superior training experience compared to traditional "dark" rinks.</li>
              </ul>
            </div>

            <div className="border-l-4 border-red-600 pl-6">
              <h3 className="text-xl font-bold text-red-600 mb-2">4. How does the "Ice-Under-Court" technology work?</h3>
              <p className="text-white text-lg">To maximize the facility's utility, the design allows for basketball and multi-sport courts to be placed directly over the ice sheets. This allows the complex to seamlessly pivot from hockey and figure skating to major court-based sports and community events without melting the ice.</p>
            </div>

            <div className="border-l-4 border-red-600 pl-6">
              <h3 className="text-xl font-bold text-red-600 mb-2">5. How will you ensure child safety and a positive culture?</h3>
              <p className="text-white text-lg">Safety is our first priority. Our operational model is built around national standards for athlete protection, including comprehensive background checks for all staff and mentors. Beyond physical security, we are dedicated to a culture of compassion—a true Place of Refuge where every child feels emotionally and physically protected.</p>
            </div>

            <div className="border-l-4 border-red-600 pl-6">
              <h3 className="text-xl font-bold text-red-600 mb-2">6. What makes the Wellness & Training Center unique?</h3>
              <p className="text-white text-lg">Our focus is on the "whole child." The plan includes a holistic development ecosystem using next-gen workstations and adaptive training software to build physical health, mental resilience, and lifelong character.</p>
            </div>

            <div className="border-l-4 border-red-600 pl-6">
              <h3 className="text-xl font-bold text-red-600 mb-2">7. When will the project open?</h3>
              <p className="text-white text-lg mb-4">We are currently in the strategic planning and development phase. Once the final site plans have gone through the necessary approval processes, we will announce our construction timeline and key milestones. Please check back for updates as we move closer to groundbreaking.</p>
              <Link to="/contact" className="inline-block bg-white border-2 border-blue-800 text-blue-700 font-bold py-3 px-6 rounded-lg hover:bg-blue-900 hover:text-white transition-colors" data-testid="faq-contact-btn">Contact Us</Link>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gray-900" data-testid="values-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center mb-12 text-red-600">Our Values</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-black p-6 rounded-lg shadow-md border-l-4 border-white" data-testid="value-compassion">
              <h3 className="text-xl font-bold text-red-600 mb-3">Compassion</h3>
              <p className="text-white">We teach children to understand and care for others, building empathy that transcends boundaries.</p>
            </div>
            <div className="bg-black p-6 rounded-lg shadow-md border-l-4 border-white" data-testid="value-excellence">
              <h3 className="text-xl font-bold text-red-600 mb-3">Excellence</h3>
              <p className="text-white">We provide world-class facilities and coaching to help every child reach their full potential.</p>
            </div>
            <div className="bg-black p-6 rounded-lg shadow-md border-l-4 border-white" data-testid="value-unity">
              <h3 className="text-xl font-bold text-red-600 mb-3">Unity</h3>
              <p className="text-white">We bring together children from diverse backgrounds, fostering understanding and friendship.</p>
            </div>
            <div className="bg-black p-6 rounded-lg shadow-md border-l-4 border-white" data-testid="value-growth">
              <h3 className="text-xl font-bold text-red-600 mb-3">Growth</h3>
              <p className="text-white">We nurture not just athletic skills, but character development and life lessons.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-black" data-testid="kids-collage-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center mb-12 text-red-600">Our Future Champions</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <img src="/hockey-team.png" alt="Youth hockey team" className="w-full h-64 object-cover rounded-lg shadow-lg" />
            <img src={`${ASSETS_URL}/kids-sports-collage.png`} alt="Kids sports collage" className="w-full h-64 object-contain rounded-lg shadow-lg bg-black border-4 border-red-600" />
            <img src={`${ASSETS_URL}/kids-track.png`} alt="Kids on track" className="w-full h-64 object-cover rounded-lg shadow-lg" />
          </div>
        </div>
      </section>

      <section className="py-20 bg-gray-900 text-white" data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6 text-red-600">Join Us in Making a Difference</h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto">Together, we can create opportunities that transform lives and build a better world for all children.</p>
          <Link to="/get-involved" className="inline-block bg-white border-2 border-blue-800 text-blue-700 px-8 py-3 rounded-full font-semibold hover:bg-blue-900 hover:text-white transition" data-testid="get-involved-cta-button">Get Involved Today</Link>
        </div>
      </section>
    </div>
  );
};

// Program Card Component
const ProgramCard = ({ title, desc, items, testId, image, subheading }) => (
  <div className="bg-gray-900 border-2 border-gray-700 rounded-lg overflow-hidden hover:shadow-xl hover:border-white transition" data-testid={testId}>
    {image && <img src={image} alt={title} className="w-full h-48 object-scale-down bg-gray-800" />}
    <div className="p-8">
      <h3 className="text-2xl font-bold text-red-600 mb-4">{title}</h3>
      <p className="text-white mb-4">{desc}</p>
      {subheading && <p className="text-white font-semibold mb-3">{subheading}</p>}
      <ul className="text-white space-y-2">
        {items.map((item, idx) => (
          <li key={idx}>• {item}</li>
        ))}
      </ul>
    </div>
  </div>
);

// Education Card Component
const EducationCard = ({ title, desc, items, testId, image }) => (
  <div className="bg-black rounded-lg shadow-md border-l-4 border-white overflow-hidden" data-testid={testId}>
    {image && <img src={image} alt={title} className="w-full h-40 object-cover object-center" />}
    <div className="p-8">
      <h3 className="text-2xl font-bold text-red-600 mb-4">{title}</h3>
      <p className="text-white mb-4">{desc}</p>
      <ul className="text-white space-y-2">
        <li>• {items[0]}</li>
        <li>• {items[1]}</li>
        <li>• {items[2]}</li>
        <li>• {items[3]}</li>
      </ul>
    </div>
  </div>
);

// Programs Page
const ProgramsPage = () => {
  return (
    <div className="programs-page bg-black text-white">
      <section className="bg-gray-900 text-white py-20" data-testid="programs-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold mb-6 text-red-600">Our Programs</h1>
          <p className="text-xl max-w-3xl mx-auto">Comprehensive programs designed to develop athletic skills, character, and leadership</p>
        </div>
      </section>

      <section className="py-20 bg-black" data-testid="sports-programs">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center mb-12 text-red-600">Sports Programs</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <ProgramCard 
              title="Comprehensive Ice Sports & Development" 
              desc={<>Beyond traditional leagues, <span className="text-red-600 italic">RMGPS</span> offers a diverse range of ice-based programming designed for all skill levels. Our "Ice Sports" umbrella covers everything from competitive youth hockey and elite figure skating to specialized edge-work clinics and community skating events. By providing dedicated ice time for multiple disciplines, we ensure a vibrant, high-energy environment that fosters athletic growth across the entire skating community.</>}
              items={[]}
              testId="program-ice-hockey"
              image="/kids-hockey.png"
            />
            <ProgramCard 
              title="Basketball" 
              desc="Professional-grade basketball courts hosting leagues, camps, and individual skill development."
              items={["Youth Leagues (Ages TBD)", "Skills Clinics", "Tournament Hosting", "Personal Training"]}
              testId="program-basketball"
              image="/sport-basketball.png"
            />
            <ProgramCard 
              title="Soccer" 
              desc="Multi-sport field accommodating soccer programs for recreational and competitive players."
              items={["Recreational Leagues", "Competitive Teams", "Summer Camps", "Coach Training"]}
              testId="program-soccer"
              image="/sport-soccer.png"
            />
            <ProgramCard 
              title="Baseball/Softball" 
              desc="Spring and summer leagues with dedicated fields and batting facilities."
              items={["Little League Programs", "Travel Teams", "Pitching Clinics", "Field Rentals"]}
              testId="program-baseball"
              image="/sport-baseball.png"
            />
            <ProgramCard 
              title="Football" 
              desc="Full-field facilities for youth football leagues and training programs."
              items={["Flag Football (Ages TBD)", "Tackle Football (Ages TBD)", "Skills Development", "Team Camps"]}
              testId="program-football"
              image="/sport-football.png"
            />
            <ProgramCard 
              title="Equestrian Programs" 
              desc="Specialized equine programs providing therapeutic benefits and character building"
              subheading="Equicentered"
              items={["Equine Centered growth and Healing", "Authentic and Compassionate growth and connection with horses", "Equine assisted Life Coaching"]}
              testId="program-equestrian"
              image="/horse-equestrian.png"
            />
          </div>
        </div>
      </section>

      <section className="py-20 bg-gray-900" data-testid="educational-programs">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center mb-12 text-red-600">Educational Programs</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <EducationCard 
              title="Charter School" 
              desc="Integrated academic programs that combine traditional education with athletic development."
              items={["Core Academic Curriculum", "STEM Programs", "Arts & Music", "Character Education"]}
              testId="program-charter-school"
              image="/edu-charter-school.png"
            />
            <EducationCard 
              title="Mentorship Programs" 
              desc="Connecting children with positive role models and mentors from diverse backgrounds."
              items={["One-on-One Mentoring", "Leadership Development", "Life Skills Workshops", "Career Exploration"]}
              testId="program-mentorship"
              image="/edu-mentorship.png"
            />
            <EducationCard 
              title="Environmental Stewardship" 
              desc="Outdoor education programs teaching children about nature and environmental responsibility."
              items={["Trail Maintenance", "Wildlife Education", "Sustainable Practices", "Outdoor Adventures"]}
              testId="program-environmental"
              image="/edu-environmental.png"
            />
            <EducationCard 
              title="Music, Arts, High Tech Creativity" 
              desc="Creative programs that complement athletic development through music, artistic expression, and cutting-edge technology."
              items={["Music Lessons", "Band & Orchestra", "Art Classes", "Performance Opportunities"]}
              testId="program-music"
              image="/edu-music-arts.png"
            />
          </div>
        </div>
      </section>

      <section className="py-20 bg-black" data-testid="summer-camps">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center mb-12 text-red-600">Coming Soon</h2>
          <div className="bg-gray-900 p-12 rounded-lg border-2 border-gray-700">
            <p className="text-xl text-center text-white mb-8">Our comprehensive programs offer children unforgettable experiences combining sports, education, and adventure in the beautiful Heber Valley.</p>
            <div className="text-center">
              <Link to="/get-involved" className="inline-block bg-white text-black px-8 py-3 rounded-full font-semibold hover:bg-gray-200 transition" data-testid="register-camps-button">Register for Programs</Link>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gray-900 text-white" data-testid="programs-cta">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6 text-red-600">Ready to Join?</h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto">Explore our programs and find the perfect fit for your child's interests and goals.</p>
          <Link to="/get-involved" className="inline-block bg-white border-2 border-blue-800 text-blue-700 px-8 py-3 rounded-full font-semibold hover:bg-blue-900 hover:text-white transition" data-testid="contact-us-button">Contact Us Today</Link>
        </div>
      </section>
    </div>
  );
};

// Get Involved Page
const GetInvolvedPage = () => {
  const [donateForm, setDonateForm] = useState({ name: '', email: '', amount: '', message: '' });
  const [volunteerForm, setVolunteerForm] = useState({ name: '', email: '', phone: '', interests: '', availability: '' });
  const [partnerForm, setPartnerForm] = useState({ organization: '', contact: '', email: '', phone: '', message: '' });

  const handleDonateSubmit = (e) => {
    e.preventDefault();
    const subject = encodeURIComponent('Donation Inquiry from Website');
    const body = encodeURIComponent(`Name: ${donateForm.name}\nEmail: ${donateForm.email}\nAmount: ${donateForm.amount}\nMessage: ${donateForm.message}`);
    window.location.href = `mailto:info@rockymountaingps.org?subject=${subject}&body=${body}`;
  };

  const handleVolunteerSubmit = (e) => {
    e.preventDefault();
    const subject = encodeURIComponent('Volunteer Application from Website');
    const body = encodeURIComponent(`Name: ${volunteerForm.name}\nEmail: ${volunteerForm.email}\nPhone: ${volunteerForm.phone}\nInterests: ${volunteerForm.interests}\nAvailability: ${volunteerForm.availability}`);
    window.location.href = `mailto:info@rockymountaingps.org?subject=${subject}&body=${body}`;
  };

  const handlePartnerSubmit = (e) => {
    e.preventDefault();
    const subject = encodeURIComponent('Partnership Inquiry from Website');
    const body = encodeURIComponent(`Organization: ${partnerForm.organization}\nContact: ${partnerForm.contact}\nEmail: ${partnerForm.email}\nPhone: ${partnerForm.phone}\nMessage: ${partnerForm.message}`);
    window.location.href = `mailto:info@rockymountaingps.org?subject=${subject}&body=${body}`;
  };

  return (
    <div className="get-involved-page bg-black text-white">
      <section className="bg-gray-900 text-white py-20" data-testid="get-involved-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold mb-6 text-red-600">Get Involved</h1>
          <p className="text-xl max-w-3xl mx-auto">Join us in creating opportunities that transform lives and build a better world for children</p>
        </div>
      </section>

      <section id="donate" className="py-20 bg-black" data-testid="donate-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <img src="/donate-heart.png" alt="Donate" className="w-64 h-auto mx-auto mb-6 rounded-lg" />
            <h2 className="text-4xl font-bold mb-4 text-red-600">Make a Donation</h2>
            <p className="text-xl text-white">Your generous contribution helps us provide world-class facilities and programs to children from all backgrounds.</p>
          </div>
          
          {/* PayPal Donate Button */}
          <div className="bg-gray-900 p-8 rounded-lg shadow-md mb-8 text-center">
            <h3 className="text-2xl font-bold mb-4 text-white">Donate with PayPal and Venmo</h3>
            <p className="text-white mb-4">Secure donations via PayPal or Venmo</p>
            <PayPalDonateButton />
          </div>
          
          <form className="bg-gray-900 p-8 rounded-lg shadow-md" data-testid="donate-form" onSubmit={handleDonateSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-white font-semibold mb-2">Full Name *</label>
                <input required className="w-full px-4 py-2 bg-black border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-white focus:border-transparent" data-testid="donate-name-input" type="text" value={donateForm.name} onChange={(e) => setDonateForm({...donateForm, name: e.target.value})} />
              </div>
              <div>
                <label className="block text-white font-semibold mb-2">Email *</label>
                <input required className="w-full px-4 py-2 bg-black border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-white focus:border-transparent" data-testid="donate-email-input" type="email" value={donateForm.email} onChange={(e) => setDonateForm({...donateForm, email: e.target.value})} />
              </div>
            </div>
            <div className="mb-6">
              <label className="block text-white font-semibold mb-2">Donation Amount *</label>
              <input required placeholder="$100" className="w-full px-4 py-2 bg-black border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-white focus:border-transparent" data-testid="donate-amount-input" type="text" value={donateForm.amount} onChange={(e) => setDonateForm({...donateForm, amount: e.target.value})} />
            </div>
            <div className="mb-6">
              <label className="block text-white font-semibold mb-2">Message (Optional)</label>
              <textarea rows="4" className="w-full px-4 py-2 bg-black border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-white focus:border-transparent" data-testid="donate-message-input" value={donateForm.message} onChange={(e) => setDonateForm({...donateForm, message: e.target.value})}></textarea>
            </div>
            <button type="submit" className="w-full bg-red-600 text-white py-3 rounded-lg font-semibold hover:bg-red-500 transition" data-testid="donate-submit-button">Submit Donation</button>
          </form>
        </div>
      </section>

      <section id="volunteer" className="py-20 bg-gray-900" data-testid="volunteer-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="flex justify-center gap-4 mb-6">
              <img src="/volunteer1.png" alt="Volunteers" className="w-48 h-auto rounded-lg" />
              <img src="/volunteer2.png" alt="Team hands" className="w-48 h-auto rounded-lg" />
            </div>
            <h2 className="text-4xl font-bold mb-4 text-red-600">Become a Volunteer</h2>
            <p className="text-xl text-white">Share your time and talents to make a direct impact on children's lives.</p>
          </div>
          <form className="bg-black p-8 rounded-lg shadow-md" data-testid="volunteer-form" onSubmit={handleVolunteerSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-white font-semibold mb-2">Full Name *</label>
                <input required className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-white focus:border-transparent" data-testid="volunteer-name-input" type="text" value={volunteerForm.name} onChange={(e) => setVolunteerForm({...volunteerForm, name: e.target.value})} />
              </div>
              <div>
                <label className="block text-white font-semibold mb-2">Email *</label>
                <input required className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-white focus:border-transparent" data-testid="volunteer-email-input" type="email" value={volunteerForm.email} onChange={(e) => setVolunteerForm({...volunteerForm, email: e.target.value})} />
              </div>
            </div>
            <div className="mb-6">
              <label className="block text-white font-semibold mb-2">Phone Number *</label>
              <input required className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-white focus:border-transparent" data-testid="volunteer-phone-input" type="tel" value={volunteerForm.phone} onChange={(e) => setVolunteerForm({...volunteerForm, phone: e.target.value})} />
            </div>
            <div className="mb-6">
              <label className="block text-white font-semibold mb-2">Areas of Interest *</label>
              <textarea required rows="3" placeholder="Coaching, mentoring, administration, facilities, etc." className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-white focus:border-transparent" data-testid="volunteer-interests-input" value={volunteerForm.interests} onChange={(e) => setVolunteerForm({...volunteerForm, interests: e.target.value})}></textarea>
            </div>
            <div className="mb-6">
              <label className="block text-white font-semibold mb-2">Availability *</label>
              <input required placeholder="Weekdays, weekends, evenings, etc." className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-white focus:border-transparent" data-testid="volunteer-availability-input" type="text" value={volunteerForm.availability} onChange={(e) => setVolunteerForm({...volunteerForm, availability: e.target.value})} />
            </div>
            <button type="submit" className="w-full bg-white border-2 border-blue-800 text-blue-700 py-3 rounded-lg font-semibold hover:bg-blue-900 hover:text-white transition" data-testid="volunteer-submit-button">Submit Volunteer Application</button>
          </form>
        </div>
      </section>

      <section id="partner" className="py-20 bg-black" data-testid="partner-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <img src="/partnership.png" alt="Support Teamwork Partnership" className="w-96 h-auto mx-auto mb-6 rounded-lg" />
            <h2 className="text-4xl font-bold mb-4 text-red-600">Become a Partner</h2>
            <p className="text-xl text-white">Join us as a corporate or community partner to amplify our impact.</p>
          </div>
          <form className="bg-gray-900 p-8 rounded-lg shadow-md" data-testid="partner-form" onSubmit={handlePartnerSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-white font-semibold mb-2">Organization Name *</label>
                <input required className="w-full px-4 py-2 bg-black border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-white focus:border-transparent" data-testid="partner-organization-input" type="text" value={partnerForm.organization} onChange={(e) => setPartnerForm({...partnerForm, organization: e.target.value})} />
              </div>
              <div>
                <label className="block text-white font-semibold mb-2">Contact Name *</label>
                <input required className="w-full px-4 py-2 bg-black border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-white focus:border-transparent" data-testid="partner-contact-input" type="text" value={partnerForm.contact} onChange={(e) => setPartnerForm({...partnerForm, contact: e.target.value})} />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-white font-semibold mb-2">Email *</label>
                <input required className="w-full px-4 py-2 bg-black border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-white focus:border-transparent" data-testid="partner-email-input" type="email" value={partnerForm.email} onChange={(e) => setPartnerForm({...partnerForm, email: e.target.value})} />
              </div>
              <div>
                <label className="block text-white font-semibold mb-2">Phone Number *</label>
                <input required className="w-full px-4 py-2 bg-black border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-white focus:border-transparent" data-testid="partner-phone-input" type="tel" value={partnerForm.phone} onChange={(e) => setPartnerForm({...partnerForm, phone: e.target.value})} />
              </div>
            </div>
            <div className="mb-6">
              <label className="block text-white font-semibold mb-2">Tell Us About Your Organization *</label>
              <textarea required rows="4" placeholder="How would you like to partner with us?" className="w-full px-4 py-2 bg-black border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-white focus:border-transparent" data-testid="partner-message-input" value={partnerForm.message} onChange={(e) => setPartnerForm({...partnerForm, message: e.target.value})}></textarea>
            </div>
            <button type="submit" className="w-full bg-blue-900 text-white py-3 rounded-lg font-semibold hover:bg-blue-800 transition" data-testid="partner-submit-button">Submit Partnership Inquiry</button>
          </form>
        </div>
      </section>
    </div>
  );
};

// Quotes Page
const QuotesPage = () => {
  const communityQuotes = [
    { text: <><span className="text-red-600">Rocky Mountain Global Peace & Sports Complex</span> will be a legacy for the State of Utah.</>, author: "Franz Kolb, Governor's Office of Economic Development" },
    { text: "We believe this project will bring tremendous benefits for children and youth in our community and state, and across the world.", author: "Joshua Jewkes, Wasatch Community Foundation" },
    { text: "This inspirational project will bring children and families from around the globe to our beautiful state & communities along the Wasatch Back.", author: "DMG, Coldwell Banker Realty" },
    { text: "Synergy is everything. As a former professional basketball player—it is not about one person, but about the collective.", author: "Lance Allred, First Deaf Player in NBA History, International inspirational speaker & best-selling author" },
    { text: <>I am honored to serve on the Board of Directors for this amazing Project. <span className="text-red-600">Rocky Mountain GPS</span> will break down barriers across the Globe for the betterment of children worldwide.</>, author: "Faith Fields, Equestrian, Teacher, Philosopher" },
    { text: <>The Wasatch County staff feels that this project aligns with the priorities of our organization, and we are looking forward to working with <span className="text-red-600">Rocky Mountain GPS</span>.</>, author: "Dustin Grabau, Wasatch County Manager" },
    { text: "From an economic development perspective, the complex will create jobs, attract tourism, and provide lasting economic benefits to the region. I believe this project will positively impact the lives of children locally and globally who will benefit from its facilities.", author: "Lori Haslem, Outreach Manager, Center for Rural Development" },
    { text: "The project reflects values central to our mission, including land preservation, trail development, and permanent infrastructure that serves youth and families across the Wasatch Back.", author: "Randon W. Wilson, Midway Heritage Foundation" },
    { text: "This project will drive economic development because it is a major investment into our community, it will create new jobs and support existing businesses, and it will help new visitors into Heber Valley.", author: "Dallin Koecher, Heber Valley Chamber of Commerce and Economic Development" },
    { text: "This project represents an investment not only in sports infrastructure, but in youth development, community engagement, and the long-term growth of figure skating in the Heber Valley... I fully support its development.", author: "Tiffany McNeil, Master-rated figure skating coach" },
    { text: "Building an ice rink in Heber Valley is about believing in our young people before they believe in themselves. When we give them a place to learn, grow and dream, we are shaping not just as athletes, but leaders and champions for life.", author: "Michelle Thomson, Promoter of Today's Youth-Tomorrow's Future" },
  ];

  const inspirationQuotes = [
    { text: "Sport has the power to change the world.", author: "Nelson Mandela" },
    { text: "If a free society cannot help the many who are poor, it cannot save those few who are rich.", author: "John F. Kennedy" },
    { text: "The true measure of any society can be found in how it treats its most vulnerable members.", author: "Mahatma Gandhi" },
    { text: "You miss 100% of the shots you don't take.", author: "Wayne Gretzky" },
    { text: "We learned about dignity and decency - that how hard you work matters more than how much you make... that helping others means more than just getting ahead yourself.", author: "Michelle Obama" },
    { text: "Empowering children is the key to a brighter tomorrow.", author: "Michael Jackson, singer, songwriter, grammy winner, philanthropist" },
    { text: "The future belongs to those who believe in the beauty of their dreams.", author: "Eleanor Roosevelt" },
    { text: "You can't put a limit on anything. The more you dream, the farther you get.", author: "Misty Copeland" },
    { text: "It's not a faith in technology. It's faith in people.", author: "Steve Jobs" },
    { text: "Success is not about being the best. It's about always getting better.", author: "Dr Marissa Mayer" },
    { text: "Knowledge is knowing that a tomato is a fruit. Wisdom is knowing not to put it in fruit salad.", author: "Brian O'Driscoll, highest scorer in all time Irish Rugby" },
    { text: "The entire point of life is to take chances on dreams that seem crazy to most but feel like destiny to you.", author: "Anonymous" },
    { text: "If you want to lift yourself up, lift someone else.", author: "Booker T. Washington" },
    { text: "When women lead, girls rise.", author: "Women's Sports Foundation" },
    { text: "When one of us falters...we all falter. When one of us rises...we all rise.", author: "Barack Obama" },
  ];

  return (
    <div className="quotes-page bg-black text-white">
      <section className="bg-gray-900 text-white py-20" data-testid="quotes-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold mb-6 text-red-600">Testimonials</h1>
          <p className="text-xl max-w-3xl mx-auto mb-8">Words of support and inspiration from our community, partners, and leaders</p>
          <div className="flex justify-center">
            <video 
              autoPlay 
              loop 
              muted 
              playsInline
              poster="/globe-hd-poster.png"
              className="w-96 h-72 object-cover rounded-lg"
              data-testid="testimonials-globe-video"
            >
              <source src="/globe-hd.mp4" type="video/mp4" />
            </video>
          </div>
        </div>
      </section>

      <section className="py-20 bg-black" data-testid="community-quotes">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center text-red-600 mb-12">Community Support</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
            {communityQuotes.map((quote, idx) => (
              <div key={idx} className="bg-gray-900 p-8 rounded-lg shadow-lg border-l-4 border-red-600">
                <blockquote className="text-white italic mb-4 text-lg leading-relaxed">"{quote.text}"</blockquote>
                <cite className="text-white font-semibold block text-right">— {quote.author}</cite>
                {quote.title && <p className="text-white text-sm text-right">{quote.title}</p>}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-gray-900" data-testid="inspiration-quotes">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center text-red-600 mb-12">Inspiration</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {inspirationQuotes.map((quote, idx) => (
              <div key={idx} className="bg-black p-6 rounded-lg shadow-lg border border-gray-700 hover:border-red-600 transition-colors">
                <blockquote className="text-white italic mb-4 leading-relaxed">"{quote.text}"</blockquote>
                <cite className="text-red-600 font-semibold block text-right text-sm">— {quote.author}</cite>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

// News Page
const NewsPage = () => {
  return (
    <div className="news-page bg-black text-white">
      <MusicPlayer />
      <section className="bg-gray-900 text-white py-20" data-testid="news-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold mb-6 text-red-600">News & Updates</h1>
          <p className="text-xl max-w-3xl mx-auto">Stay informed about the latest happenings at <span className="text-red-600 italic">Rocky Mountain GPS</span></p>
        </div>
      </section>

      {/* Updates Collage */}
      <section className="py-12 bg-black">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div>
              <img src="/news-collage.png" alt="Construction Updates" className="w-full rounded-lg" />
            </div>
            <div>
              <h3 className="text-xl font-bold mb-4 text-red-600">Sprung Structures Build</h3>
              <div style={{position: 'relative', paddingBottom: '56.25%', height: 0, overflow: 'hidden', borderRadius: '8px'}}>
                <iframe 
                  style={{position: 'absolute', top: 0, left: 0, width: '100%', height: '100%'}}
                  src="https://www.youtube.com/embed/UevG0UGlkj4?start=70&end=78&mute=1&rel=0&modestbranding=1"
                  title="Sprung Structures Build"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-8 bg-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xl text-white">More updates coming soon!</p>
        </div>
      </section>

      {/* Sprung Structures Link */}
      <section className="py-16 bg-gray-900" data-testid="sprung-link-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="text-2xl font-bold mb-4 text-red-600">See Our Building Partner in Action</h3>
          <p className="text-white mb-6">Learn more about Sprung Structures and their innovative sports facilities.</p>
          <a href="https://www.sprung.com/projects/edge-school-sports-program/" target="_blank" rel="noopener noreferrer" className="inline-block bg-gray-900 border-2 border-red-600 text-red-600 px-8 py-3 rounded-full font-semibold hover:bg-red-600 hover:text-white transition">Visit Sprung Structures</a>
        </div>
      </section>
    </div>
  );
};

// Contact Page
const ContactPage = () => {
  const [contactForm, setContactForm] = useState({ name: '', email: '', phone: '', reason: '', message: '' });

  const handleContactSubmit = (e) => {
    e.preventDefault();
    const subject = encodeURIComponent(`${contactForm.reason} - Contact from Website`);
    const body = encodeURIComponent(`Name: ${contactForm.name}\nEmail: ${contactForm.email}\nPhone: ${contactForm.phone}\nReason: ${contactForm.reason}\nMessage: ${contactForm.message}`);
    window.location.href = `mailto:info@rockymountaingps.org?subject=${subject}&body=${body}`;
  };

  return (
    <div className="contact-page bg-black text-white">
      <MusicPlayer />
      <section className="bg-gray-900 text-white py-20" data-testid="contact-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold mb-6 text-red-600">Contact Us</h1>
          <p className="text-xl max-w-3xl mx-auto mb-8">We'd love to hear from you</p>
          <img src="/locker-room.png" alt="Youth hockey team in locker room" className="max-w-lg mx-auto rounded-lg shadow-xl" />
        </div>
      </section>

      <section className="py-20 bg-black">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div>
              <h2 className="text-3xl font-bold text-red-600 mb-8">Get In Touch</h2>
              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-semibold text-white mb-2">Address</h3>
                  <p className="text-white">854 N 50 W Alfalfa Circle</p>
                  <p className="text-white">Midway, Utah 84049</p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-white mb-2">Email</h3>
                  <a href="mailto:info@rockymountaingps.org" className="text-white hover:text-white transition">info@rockymountaingps.org</a>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-white mb-2">Phone</h3>
                  <a href="tel:435.709.6445" className="text-white hover:text-white transition">435.709.6445</a>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <form className="bg-gray-900 p-8 rounded-lg shadow-md" onSubmit={handleContactSubmit}>
                <div className="mb-6">
                  <label className="block text-white font-semibold mb-2">Full Name *</label>
                  <input required className="w-full px-4 py-2 bg-black border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-white focus:border-transparent" type="text" value={contactForm.name} onChange={(e) => setContactForm({...contactForm, name: e.target.value})} />
                </div>
                <div className="mb-6">
                  <label className="block text-white font-semibold mb-2">Email *</label>
                  <input required className="w-full px-4 py-2 bg-black border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-white focus:border-transparent" type="email" value={contactForm.email} onChange={(e) => setContactForm({...contactForm, email: e.target.value})} />
                </div>
                <div className="mb-6">
                  <label className="block text-white font-semibold mb-2">Phone</label>
                  <input className="w-full px-4 py-2 bg-black border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-white focus:border-transparent" type="tel" value={contactForm.phone} onChange={(e) => setContactForm({...contactForm, phone: e.target.value})} />
                </div>
                <div className="mb-6">
                  <label className="block text-white font-semibold mb-2">Reason for Contact *</label>
                  <select required className="w-full px-4 py-2 bg-black border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-white focus:border-transparent" value={contactForm.reason} onChange={(e) => setContactForm({...contactForm, reason: e.target.value})}>
                    <option value="">Select a reason...</option>
                    <option value="General Inquiry">General Inquiry</option>
                    <option value="Information on Contributors">Information on Our Contributors</option>
                    <option value="Volunteering">Volunteering</option>
                    <option value="Partnership/Sponsorship">Partnership/Sponsorship</option>
                    <option value="Donation Inquiry">Donation Inquiry</option>
                    <option value="Media/Press">Media/Press</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div className="mb-6">
                  <label className="block text-white font-semibold mb-2">Message *</label>
                  <textarea required rows="5" className="w-full px-4 py-2 bg-black border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-white focus:border-transparent" value={contactForm.message} onChange={(e) => setContactForm({...contactForm, message: e.target.value})}></textarea>
                </div>
                <button type="submit" className="w-full bg-white text-black py-3 rounded-lg font-semibold hover:bg-gray-200 transition">Send Message</button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Special Thanks Section - Video */}
      <section className="py-20 bg-black" data-testid="special-thanks-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold mb-8 text-red-600 text-center">Special Thanks</h2>
          <div className="rounded-lg overflow-hidden shadow-xl">
            <video 
              autoPlay 
              loop 
              muted 
              playsInline
              className="w-full"
              data-testid="thank-you-video"
            >
              <source src="/thank-you-video-new.mp4" type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
          <p className="text-white text-center mt-8 text-lg">
            We are incredibly grateful for the expertise and support of the individuals and organizations listed above. If you would like to connect with any of our contributors regarding their work or professional services, please reach out to us through our <Link to="/contact" className="text-red-500 underline hover:text-red-400">Contact Form</Link> and we would be happy to facilitate an introduction.
          </p>
        </div>
      </section>
    </div>
  );
};

// Revenue Page
const RevenuePage = () => {
  return (
    <div className="revenue-page bg-black text-white">
      
      {/* Hero */}
      <section className="bg-gray-900 text-white py-12" data-testid="revenue-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div className="text-center md:text-left">
              <h1 className="text-5xl font-bold mb-6 text-red-600">Economic Impact</h1>
              <p className="text-xl text-white">The <span className="text-red-600 italic">RMGPS Complex</span> serves as a premier destination for national-caliber events that celebrate competition, culture, and community. By hosting signature winter classics and immersive festivals, we create a year-round engine for regional growth and Olympic-level engagement.</p>
            </div>
            <div>
              <img src="/events-hero.png" alt="Heber Valley Winter Scene" className="w-full rounded-lg shadow-xl" />
            </div>
          </div>
        </div>
      </section>

      {/* Wasatch Winter Classic */}
      <section className="py-12 bg-black">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div>
              <h2 className="text-3xl font-bold text-red-600 mb-4">Wasatch Winter Classic</h2>
              <p className="text-white italic mb-6">A return to the roots of the game. This community-driven outdoor pond hockey tournament is a centerpiece of our winter season, celebrating the spirit of competition on natural ice.</p>
              <div className="space-y-4">
                <div className="bg-gray-900 p-4 rounded-lg">
                  <h3 className="text-white font-bold mb-1">Scale & Scope</h3>
                  <p className="text-white text-sm">A three-day event featuring 100 teams across Youth, Adult Open, and Co-Ed divisions.</p>
                </div>
                <div className="bg-gray-900 p-4 rounded-lg">
                  <h3 className="text-white font-bold mb-1">Community Impact</h3>
                  <p className="text-white text-sm">Expected to draw 2,000–3,000 total attendees, modeled after the successful New England Pond Hockey Classic.</p>
                </div>
                <div className="bg-gray-900 p-4 rounded-lg">
                  <h3 className="text-white font-bold mb-1">2034 Olympic Momentum</h3>
                  <p className="text-white text-sm">As national teams seek pre-Olympic training sites, these classics build direct momentum toward the 2034 Games.</p>
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <img src="/event2.jpg" alt="Pond Hockey Rinks" className="w-full h-40 object-cover rounded-lg shadow-xl" />
              <img src="/event3.jpg" alt="Aerial Pond Hockey" className="w-full h-40 object-cover rounded-lg shadow-xl" />
            </div>
          </div>
        </div>
      </section>

      {/* GLACIUM */}
      <section className="py-12 bg-gray-900">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div className="space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <img src="/event4.png" alt="Glass Christmas Tree" className="w-full h-80 object-contain rounded-lg shadow-xl bg-black" />
                <div className="space-y-2">
                  <img src="/event5.png" alt="Glass Blower" className="w-full h-40 object-cover rounded-lg shadow-xl" />
                  <img src="/event6.jpg" alt="Holiday Market" className="w-full h-36 object-cover rounded-lg shadow-xl" />
                </div>
              </div>
            </div>
            <div>
              <h2 className="text-3xl font-bold text-red-600 mb-4">GLACIUM: Winter Festival of Wonder</h2>
              <p className="text-white italic mb-6">Castles of Ice, Realms of Wonder, Forged in Glass & Glow</p>
              <div className="space-y-4">
                <div className="bg-black p-4 rounded-lg">
                  <h3 className="text-white font-bold mb-1">Realms of Wonder</h3>
                  <p className="text-white text-sm">Immersive walkthrough experiences featuring intricate ice archways, frozen mazes, and monumental sculptures.</p>
                </div>
                <div className="bg-black p-4 rounded-lg">
                  <h3 className="text-white font-bold mb-1">Holiday Landmarks</h3>
                  <p className="text-white text-sm">Home to the region's tallest glass Christmas tree and a festive holiday market featuring local artisans and glass-blowers.</p>
                </div>
                <div className="bg-black p-4 rounded-lg">
                  <h3 className="text-white font-bold mb-1">Public Draw</h3>
                  <p className="text-white text-sm">A seasonal destination designed to bring families and visitors together in the heart of the Heber Valley.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Western Heritage */}
      <section className="py-12 bg-black">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div>
              <h2 className="text-3xl font-bold text-red-600 mb-4">Western Heritage & Arts Festival</h2>
              <p className="text-white italic mb-6">A multi-day celebration honoring the spirit and history of the American West, modeled after the world-renowned gathering in Elko, Nevada.</p>
              <div className="space-y-4">
                <div className="bg-gray-900 p-4 rounded-lg">
                  <h3 className="text-white font-bold mb-1">Poetry & Music</h3>
                  <p className="text-white text-sm">Live performances featuring cowboy poetry, Western music, and storytelling sessions.</p>
                </div>
                <div className="bg-gray-900 p-4 rounded-lg">
                  <h3 className="text-white font-bold mb-1">Ranch Culture</h3>
                  <p className="text-white text-sm">A gathering place for families, ranchers, and historians to share the heritage of the land through Western art exhibitions and gear craftsmanship.</p>
                </div>
                <div className="bg-gray-900 p-4 rounded-lg">
                  <h3 className="text-white font-bold mb-1">Cultural Connection</h3>
                  <p className="text-white text-sm">Dedicated to preserving regional identity through Western film screenings and culinary showcases.</p>
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <img src="/event7.jpg" alt="Cowboy Poetry" className="w-full h-40 object-cover rounded-lg shadow-xl" />
              <img src="/event8.jpg" alt="Western Festival" className="w-full h-40 object-cover rounded-lg shadow-xl" />
            </div>
          </div>
        </div>
      </section>

      {/* Future Tournament Potential */}
      <section className="py-12 bg-gray-900">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-red-600 mb-8">Future Tournament Potential</h2>
          
          <div className="bg-gray-800 rounded-lg p-8">
            {/* Header with images */}
            <div className="flex flex-col md:flex-row items-center justify-between mb-8">
              <div className="flex items-center gap-4 mb-4 md:mb-0">
                <img src="/rmgps-basketball-court.png" alt="Rocky Mountain GPS Basketball Court" className="w-48 rounded-lg" />
              </div>
              <div className="text-center">
                <h3 className="text-2xl font-bold text-red-600">FUTURE TOURNAMENT POTENTIAL <span className="text-red-600">(TBD)</span></h3>
                <p className="text-white">National Comparables for Hockey & Basketball</p>
              </div>
              <div>
                <img src="/girl-basketball.png" alt="Youth Basketball Player" className="w-32 rounded-lg" />
              </div>
            </div>
            
            {/* Two columns for comparables */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
              <div>
                <h4 className="text-red-600 font-bold mb-4">HOCKEY COMPARABLES:</h4>
                <ul className="text-white text-sm space-y-2">
                  <li>• CCM World Invite (Chicago) - Largest youth hockey tournament globally</li>
                  <li>• Chi-Town Shuffle (Chicago) - Major spring AAA/elite showcase</li>
                  <li>• CAN/AM Challenge Cup (Lake Placid) - International youth & girls</li>
                  <li>• Super Series AAA (Pittsburgh, Nashville) - Elite AAA showcases</li>
                  <li>• USA Hockey Nationals - Championship events, national qualifiers</li>
                  <li>• NGHL Girls Showcases - Dedicated girls' pathway events</li>
                </ul>
              </div>
              <div>
                <h4 className="text-red-600 font-bold mb-4">BASKETBALL COMPARABLES:</h4>
                <ul className="text-white text-sm space-y-2">
                  <li>• USBA National Championship - Premier youth nationals</li>
                  <li>• AAU Basketball Nationals - Multiple age divisions</li>
                  <li>• Nike EYBL - Elite Youth Basketball League</li>
                  <li>• Adidas Gauntlet Series - National travel circuit</li>
                  <li>• Run 4 the Roses (Louisville) - Major girls' recruiting showcase</li>
                </ul>
              </div>
            </div>
            
            {/* Projected Impact */}
            <div className="text-center mb-6">
              <h4 className="text-red-600 font-bold mb-4">Projected Impact If Secured:</h4>
              <div className="flex justify-center gap-12">
                <div>
                  <p className="text-white text-3xl font-bold">$39M+</p>
                  <p className="text-red-600 text-sm">Direct Spend</p>
                </div>
                <div>
                  <p className="text-white text-3xl font-bold">$4.9M+</p>
                  <p className="text-red-600 text-sm">Tax Revenue</p>
                </div>
                <div>
                  <p className="text-white text-3xl font-bold">15,000+</p>
                  <p className="text-red-600 text-sm">Room Nights</p>
                </div>
              </div>
            </div>
            
            <p className="text-gray-400 text-xs text-center">These national tournaments represent the scale and economic impact Utah could attract. Specific events and timing to be determined.</p>
          </div>
        </div>
      </section>

      {/* Partner CTA */}
      <section className="py-16 bg-black text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6 text-red-600">Partner With Us</h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto">Join us in building a world-class facility that will drive economic growth and transform lives.</p>
          <Link to="/get-involved" className="inline-block bg-white border-2 border-blue-800 text-blue-700 px-8 py-3 rounded-full font-semibold hover:bg-blue-900 hover:text-white transition">Get Involved</Link>
        </div>
      </section>
    </div>
  );
};

// Facilities Page
const FacilitiesPage = () => {
  return (
    <div className="facilities-page bg-black text-white">
      <section className="py-8 bg-black">
        <div className="max-w-5xl mx-auto px-4">
          <h1 className="text-4xl font-bold text-red-600 text-center mb-8">The Complex</h1>
          <img src="/facility-main.png" alt="Rocky Mountain Global Peace and Sports Complex" className="w-full rounded-lg shadow-xl" />
        </div>
      </section>

      <section className="py-16 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Center Building */}
          <div className="mb-16 bg-gray-900 rounded-lg overflow-hidden">
            <div className="bg-gray-800 p-4">
              <h2 className="text-3xl font-bold text-white">Center Building</h2>
            </div>
            <div className="p-6">
              <img src="/facility-center.png" alt="Center Building" className="w-full max-w-md mx-auto rounded-lg shadow-xl mb-6" />
              <ul className="text-white space-y-3">
                <li>• One Full Size (Regulation Expandable NHL) sheet of ice</li>
                <li>• Includes state-of-the-art seating, premier locker room facilities for both male and female athletes, and referees</li>
                <li>• State-of-the-art score board</li>
                <li>• Advanced technology will be installed to accommodate disabled, hearing-impaired athletes and guests</li>
                <li>• This building will host national youth, adult and disabled tournaments</li>
              </ul>
            </div>
          </div>

          {/* West Building */}
          <div className="mb-16 bg-gray-900 rounded-lg overflow-hidden">
            <div className="bg-gray-800 p-4">
              <h2 className="text-3xl font-bold text-white">West Building</h2>
            </div>
            <div className="p-6">
              <img src="/hockey-action.png" alt="West Building" className="w-full max-w-md mx-auto rounded-lg shadow-xl mb-6" />
              <ul className="text-white space-y-3">
                <li>• One Full size (Regulation NHL size) sheet of ice for curling, figure skating, broom ball, etc.</li>
                <li>• One Half Sheet of Ice for pee-wee, bantam and younger children. (Option to replace Ice with regulation size basketball court)</li>
                <li>• One Five-Star Restaurant</li>
              </ul>
            </div>
          </div>

          {/* East Field */}
          <div className="mb-16 bg-gray-900 rounded-lg overflow-hidden">
            <div className="bg-gray-800 p-4">
              <h2 className="text-3xl font-bold text-white">East Field (Outdoor Athletics)</h2>
            </div>
            <div className="p-6">
              <img src="/facility-east.jpg" alt="East Field - Outdoor Athletics" className="w-full max-w-md mx-auto rounded-lg shadow-xl mb-6" />
              <ul className="text-white space-y-3">
                <li>• One Large Outdoor Field for soccer, baseball, lacrosse, rugby and football</li>
                <li>• Covered in state-of-the-art Turf</li>
                <li>• Eight Lane running track surrounding the perimeter</li>
              </ul>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

// Leadership Page
const LeadershipPage = () => {
  const column1 = [
    { name: "RANDON W. WILSON", title: "Board Chair", bio: "50+ years as corporate attorney. Counsel for Line-Item Veto Law challenge US Supreme Court. General Counsel for Zermatt Resort development." },
    { name: "LANCE ALLRED", title: "", bio: "TEDx speaker with 4.6M+ views. Founded Courage & Grit youth development programs. International motivational speaker on perseverance & leadership." },
    { name: "FAITH FIELDS", title: "", bio: "USEF & FEI Licensed Official with 25+ years teaching experience. Leading equestrian healing and training programs development." },
  ];
  
  const column2 = [
    { name: "LAURIE NEAL", title: "Executive Director", bio: "Founder, thunderHeart, Inc. Film & Television Production; Non-Profit Board Of Director Experience; Grant writing & economic development experience; Midway 2002 Olympics coordinator; Charity Event Management" },
    { name: "TRENT ROUNKLES", title: "", bio: "Founder of Riverfront Roofing. Baseball coach-Morgan High School. Partner at Wasatch Sports Academy. Expertise in mentorship & athlete development." },
    { name: "RYAN SHUFLIN", title: "", bio: "Founder of RCO Construction Services. 28 years iron work expertise. Youth hockey coach instrumental in bringing organized hockey to the Wasatch Front" },
  ];
  
  const column3 = [
    { name: "NEIL SMITH", title: "", bio: "Led the NY Rangers to their first Stanley Cup in 54 years (1994). Renowned NHL talent evaluator; President and General Manager of the 1994 Stanley Cup Champions, The New York Rangers." },
    { name: "KRISTA J. CLARK", title: "", bio: "Expertise in ownership and operations of a physical therapy center; co-founder of multidisciplinary integrative health center. Krista is set to design our Wellness Center." },
    { name: "DEBORAH HICKEY", title: "", bio: "Extensive background in Figure Skating and Photography; Broadway performer; developer of training programs throughout the US and Canada." },
  ];
  
  const patrickHickey = {
    name: "PATRICK HICKEY",
    bio: "NHL Veteran, Patrick brings elite-level discipline. He is celebrated as 'Miracle Maker' for his 30+ years of expertise of establishing high-impact youth advocacy systems including the innovative Ice Hockey in Harlem programs."
  };

  const letters = [
    { name: "Franz Kolb", title: "Governor's Office of Economic Opportunity", quote: <><span className="text-red-600">Rocky Mountain Global Peace & Sports Complex</span> will be a legacy for the State of Utah...</> },
    { name: "Lance Allred", title: "First Deaf NBA Player, Board Member", quote: "Synergy is everything. By bringing these diverse programs together under one roof, we create unique opportunities for growth, learning, and healing across generations..." },
    { name: "Joshua D. Jewkes", title: "Wasatch Community Foundation", quote: "This project aligns perfectly with our community's values and development goals, creating opportunities for our youth while respecting our natural resources..." },
    { name: "Lori Haslem", title: "Outreach Manager, Center for Rural Development", quote: "This project represents the kind of sustainable economic development that rural Utah communities need - balanced growth that creates opportunities without sacrificing" },
    { name: "Randon Wilson", title: "Secretary Midway Heritage Foundation", quote: "This project reflects values central to our mission, including land preservation, trail development, and permanent infrastructure that serves youth and families across the Wasatch Back." },
    { name: "Tiffany McNeil", title: "Master-rated figure skating coach", quote: "This project represents an investment not only in sports infrastructure, but in youth development, community engagement, and the long-term growth of figure skating in the Heber Valley... I fully support its development." },
    { name: "Faith Fields", title: "Equestrian, Teacher, Philosopher", quote: <>I am honored to serve on the Board... <span className="text-red-600">RMGPS</span> breaks down barriers across the Globe for the betterment of children and youth together with one common goal and that is to make the world a better place to live.</> },
    { name: "DMG", title: "Coldwell Banker Realty", quote: <>We at DMG and Coldwell Banker Realty are thrilled to be involved in the initial phases of the <span className="text-red-600">Rocky Mountain GPS</span> Complex development. The inspirational project will bring children and families from around the globe to our beautiful communities along the Wasatch Back, and we couldn't be more excited to be a part of it.</> },
    { name: "Dallin Koecher", title: "Heber Valley Chamber Of Commerce", quote: "This project will drive economic development because it is a major investment into our community. It will create new jobs and support existing businesses, and it will help attract new visitors into Heber Valley." },
    { name: "Dustin Grabau", title: "Wasatch County Manager", quote: <>Wasatch County enthusiastically supports the <span className="text-red-600">Rocky Mountain GPS</span> project and its vision for youth development through sports...</> },
    { name: "Ashley A. Burr", title: "Program Director, MIDA Mountain Veterans", quote: "We really wish you the best and think this would be an economic and recreational boon to the Wasatch Back. It is a great project that we hope to see come to fruition." },
    { name: "Michelle Thomson", title: "Promoter of Today's Youth-Tomorrow's Future", quote: "Building an ice rink in Heber Valley is about believing in our young people before they believe in themselves. When we give them a place to learn, grow and dream, we are shaping not just as athletes, but leaders and champions for life." },
  ];

  return (
    <div className="leadership-page bg-black text-white">
      
      {/* Leadership Section */}
      <section className="py-16 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl font-bold text-red-600 text-center mb-4">10. Leadership and Community Support</h1>
          <p className="text-white text-center mb-12">World-class sports and business expertise driving our mission</p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Column 1 */}
            <div className="space-y-6">
              {column1.map((leader, idx) => (
                <div key={idx} className="bg-gray-900 p-6 rounded-lg">
                  <h3 className="text-xl font-bold text-red-600">{leader.name}</h3>
                  {leader.title && <p className="text-white font-semibold mb-2">{leader.title}</p>}
                  <p className="text-white text-sm">{leader.bio}</p>
                </div>
              ))}
            </div>
            {/* Column 2 */}
            <div className="space-y-6">
              {column2.map((leader, idx) => (
                <div key={idx} className="bg-gray-900 p-6 rounded-lg">
                  <h3 className="text-xl font-bold text-red-600">{leader.name}</h3>
                  {leader.title && <p className="text-white font-semibold mb-2">{leader.title}</p>}
                  <p className="text-white text-sm">{leader.bio}</p>
                </div>
              ))}
            </div>
            {/* Column 3 */}
            <div className="space-y-6">
              {column3.map((leader, idx) => (
                <div key={idx} className="bg-gray-900 p-6 rounded-lg">
                  <h3 className="text-xl font-bold text-red-600">{leader.name}</h3>
                  {leader.title && <p className="text-white font-semibold mb-2">{leader.title}</p>}
                  <p className="text-white text-sm">{leader.bio}</p>
                </div>
              ))}
            </div>
          </div>
          
          {/* Patrick Hickey - Narrower Width */}
          <div className="mt-6 bg-gray-900 p-6 rounded-lg max-w-3xl mx-auto">
            <h3 className="text-xl font-bold text-red-600">{patrickHickey.name}</h3>
            <p className="text-white text-sm mt-2">{patrickHickey.bio}</p>
          </div>
          
          <div className="mt-8 text-center">
            <p className="text-red-600 font-semibold italic">thunderHeart, Inc. A 501(c)(3) non-for-profit registered with the IRS since 2001.</p>
            <p className="text-white">Two decades of youth development through sports and education</p>
          </div>
        </div>
      </section>

      {/* Letters of Support Section */}
      <section className="py-16 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-red-600 text-center mb-4">Letters of Support</h2>
          <p className="text-white text-center mb-12">Stakeholders and partners endorsing our vision</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {letters.map((letter, idx) => (
              <div key={idx} className="bg-black p-6 rounded-lg border-l-4 border-red-600">
                <p className="text-white italic mb-4">"{letter.quote}"</p>
                <p className="text-red-600 font-bold">{letter.name}</p>
                <p className="text-white text-sm">{letter.title}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

// Investment Page
const InvestmentPage = () => {
  return (
    <div className="investment-page bg-black text-white">
      
      {/* Hero */}
      <section className="py-12 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-2xl text-white italic mb-4">A better way to build...a better way to grow.</p>
          <p className="text-xl text-red-500 italic mb-4">Together, these partnerships enable a once-in-a-generation investment in Utah's youth, economy, and future.</p>
          <h1 className="text-5xl font-bold text-red-600 mb-2">Your Investment Creates Legacy</h1>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            
            {/* Left Column - Investment Options */}
            <div>
              <h2 className="text-3xl font-bold text-white mb-8">Investment Options</h2>
              
              {/* Option 1 */}
              <div className="flex mb-6">
                <div className="bg-red-600 text-white text-2xl font-bold w-12 h-auto flex items-center justify-center rounded-l-lg">1</div>
                <div className="bg-gray-800 p-4 rounded-r-lg flex-1 border-l-4 border-red-600">
                  <h3 className="text-xl font-bold text-white mb-2">Full Project Build</h3>
                  <p className="text-white">Anticipated completion within 1.5-2 years from full funding</p>
                  <p className="text-white text-sm">maximum economic impact immediately, full programming capacity, unified construction timeline</p>
                </div>
              </div>

              {/* Option 2 */}
              <div className="flex mb-6">
                <div className="bg-red-600 text-white text-2xl font-bold w-12 h-auto flex items-center justify-center rounded-l-lg">2</div>
                <div className="bg-gray-800 p-4 rounded-r-lg flex-1 border-l-4 border-red-600">
                  <h3 className="text-xl font-bold text-white mb-2">Co-Investment Blend</h3>
                  <ul className="text-white text-sm space-y-1">
                    <li>• Strategic partnership of funders</li>
                    <li>• Mix of state/private/corporate/foundation support</li>
                    <li>• Shared naming rights and recognition</li>
                    <li>• Collaborative governance structure</li>
                  </ul>
                </div>
              </div>

              {/* Option 3 */}
              <div className="flex mb-8">
                <div className="bg-red-600 text-white text-2xl font-bold w-12 h-auto flex items-center justify-center rounded-l-lg">3</div>
                <div className="bg-gray-800 p-4 rounded-r-lg flex-1 border-l-4 border-red-600">
                  <h3 className="text-xl font-bold text-white mb-2">Phased Approach</h3>
                  <p className="text-white"><span className="text-red-500 font-bold">Phase 1 & Phase 2:</span> Protocol if full funding not achieved</p>
                </div>
              </div>

              {/* Schedule Discussion */}
              <div className="mt-8">
                <h3 className="text-2xl font-bold text-red-600 underline mb-4">Schedule a Partnership Discussion</h3>
                <p className="text-white"><span className="font-bold">Contact Us:</span> <a href="mailto:info@rockymountaingps.org" className="text-white hover:text-white">info@rockymountaingps.org</a> or 435.709.6445</p>
                <p className="text-white mt-2"><span className="font-bold">Identity:</span> <span className="text-white italic">thunderHeart, Inc. 501(c)(3) Heber Valley, Utah</span></p>
                <Link to="/contact" className="inline-block mt-4 bg-white border-2 border-blue-800 text-blue-700 px-6 py-2 rounded-full font-semibold hover:bg-blue-900 hover:text-white transition-colors">Contact Us</Link>
              </div>
            </div>

            {/* Right Column - What Investment Enables */}
            <div>
              <h2 className="text-2xl font-bold text-white mb-6">What Your Investment Enables</h2>
              <ul className="text-white space-y-2 mb-8">
                <li>• 2.5 NHL SHEETS OF ICE SERVING 12,000+ YOUTH</li>
                <li>• CHARTER SCHOOL INTEGRATION WITH ATHLETICS</li>
                <li>• BASKETBALL COURTS AND COMMUNITY ACCESS</li>
                <li>• $15M SCHOLARSHIP ENDOWMENT FOR BARRIER FREE ACCESS</li>
                <li>• 1,115 CONSTRUCTION JOBS +175-235 PERMANENT JOBS</li>
                <li>• $26M TAX REVENUE OVER 10 YEARS</li>
                <li>• 15,000+ ANNUAL ROOM NIGHTS</li>
              </ul>

              <h2 className="text-2xl font-bold text-white mb-6">Funding Sources Sought:</h2>
              <ul className="text-white space-y-2 mb-8">
                <li>• State appropriations</li>
                <li>• Private foundation grants</li>
                <li>• Corporate partnerships and naming rights</li>
                <li>• Individual major donors</li>
                <li>• Private foundation grants</li>
              </ul>

              {/* Call to Action */}
              <div className="mt-8">
                <h3 className="text-2xl font-bold text-red-600 mb-4">CALL TO ACTION</h3>
                <p className="text-white">Your investment will create a legacy of opportunity, inclusion, and excellence for generations to come.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Bottom Banner */}
      <section className="py-8 bg-black border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-red-500 italic text-lg">"A community is measured by how it invests in its children's future."</p>
        </div>
      </section>
    </div>
  );
};

// Imagine Page - Turn Imagination Into Reality
const ImaginePage = () => {
  const imagineBoxes = [
    {
      text: "An unparalleled sports and training complex where kids from around the globe can play together and bond together."
    },
    {
      text: "Kids from wide-ranging economic, social, and political backgrounds learning life lessons of sports through teamwork, mindfulness, and creativity."
    },
    {
      text: "Urban and rural kids from North America coming together with kids from international communities through our unique scholarship program."
    },
    {
      text: "Comprehensive programs developed by former NBA player Lance Allred and dedicated mentors like Neil Smith, former President of the Stanley Cup New York Rangers, and Patrick Hickey, former NHL player. Programs that will inspire kids to not only be better athletes, but to promote positive change in their own communities."
    },
  ];

  return (
    <div className="imagine-page bg-black text-white">
      <MusicPlayer autoPlay={true} />
      
      {/* Hero Title */}
      <section className="py-12 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-red-600 tracking-wide">TURN IMAGINATION INTO REALITY</h1>
        </div>
      </section>

      {/* Imagine Boxes Grid */}
      <section className="py-8 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Top Row - 3 boxes */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            {imagineBoxes.slice(0, 3).map((box, idx) => (
              <div key={idx} className="border-2 border-red-600 rounded-lg p-6 text-center">
                <p className="text-gray-200">
                  <span className="text-red-500 italic font-bold">Imagine... </span>
                  {box.text}
                </p>
              </div>
            ))}
          </div>
          
          {/* Bottom Row - Comprehensive Programs (wider) in center */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
            <div className="md:col-span-1"></div>
            <div className="border-2 border-red-600 rounded-lg p-6 text-center md:col-span-3">
              <p className="text-gray-200">
                <span className="text-red-500 italic font-bold">Imagine... </span>
                {imagineBoxes[3].text}
              </p>
            </div>
            <div className="md:col-span-1"></div>
          </div>
          
          {/* And Finally Imagine - centered below */}
          <div className="mt-6 max-w-xl mx-auto">
            <div className="border-2 border-red-600 rounded-lg p-6 text-center">
              <p className="text-gray-200">
                <span className="text-red-500 italic font-bold">And finally, imagine... </span>
                all this located in the beautiful Heber Valley, the heart of Utah's Mountain Adventure.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Champions Who Believe */}
      <section className="py-12 bg-black">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-red-600 mb-8">Champions Who Believe</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-start">
            <div>
              <div className="rounded-lg overflow-hidden border-2 border-red-600 bg-black h-96">
                <img src="/lance-allred-clean.png" alt="Lance Allred - Former NBA Player" className="w-full h-full object-contain" />
              </div>
              <p className="text-white text-lg mt-3">Lance Allred</p>
              <p className="text-white text-sm">Former NBA Player</p>
            </div>
            <div>
              <div className="rounded-lg overflow-hidden border-2 border-red-600 bg-black h-96">
                <img src="/patrick-hickey-new.png" alt="Patrick Hickey - Former NHL Player" className="w-full h-full object-contain" style={{transform: 'scaleX(1.12)'}} />
              </div>
              <p className="text-white text-lg mt-3">Patrick Hickey</p>
              <p className="text-white text-sm">Former NHL Player</p>
            </div>
            <div>
              <div className="rounded-lg overflow-hidden border-2 border-red-600 bg-black h-96">
                <img src="/neil-smith-new.png" alt="Neil Smith - Former President, New York Rangers" className="w-full h-full object-contain" style={{transform: 'scale(1.2)'}} />
              </div>
              <p className="text-white text-lg mt-3">Neil Smith</p>
              <p className="text-white text-sm">Former President NY Rangers</p>
            </div>
          </div>
        </div>
      </section>

      {/* The Complex Section */}
      <section className="py-12 bg-black">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-6">
            <p className="text-gray-200">
              <span className="text-red-600 font-bold">The Complex: </span>
              This multi-purpose sports complex will include 2.5 ice rinks, two basketball courts, a charter School and two dormitories, a wellness and training facility, a multi-sport and track field, and lots of open space via a connecting trail system.
            </p>
            
            <p className="text-gray-200">
              <span className="text-red-600 font-bold">Identity: </span>
              We call this the <span className="text-red-600 italic">Rocky Mountain Global Peace and Sports Complex</span> - and there's nothing like it anywhere in the world.
            </p>
            
            <p className="text-2xl text-center mt-8">
              <span className="text-red-500 italic font-bold">Imagine... </span>
              <span className="text-white italic">the global impact on kids and their communities.</span>
            </p>
            
            <div className="flex justify-center mt-8">
              <img src="/kids-diverse-smiling.png" alt="Diverse kids smiling" className="w-full max-w-2xl rounded-lg" />
            </div>
          </div>
        </div>
      </section>

      {/* Bottom Banner */}
      <section className="py-16 bg-black border-t border-gray-800">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-2xl md:text-3xl text-white font-bold mb-4">
            A PLACE WHERE NO CHILD IS DENIED ACCESS BECAUSE OF PHYSICAL OR SOCIO-ECONOMIC BACKGROUNDS... IT'S CALLED
          </p>
          <p className="text-3xl md:text-4xl lg:text-4xl text-red-600 font-bold italic whitespace-nowrap">
            ROCKY MOUNTAIN GLOBAL PEACE & SPORTS COMPLEX
          </p>
        </div>
      </section>

      {/* Banner Globe Video */}
      <section className="py-12 bg-black" data-testid="banner-video-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="border-2 border-red-600 rounded-lg overflow-hidden">
            <video 
              className="w-full"
              controls
              autoPlay
              muted
              loop
              playsInline
              data-testid="banner-globe-video"
              style={{minHeight: '300px', backgroundColor: '#000', display: 'block'}}
            >
              <source src="/banner-globe.mp4" type="video/mp4" />
              <p className="text-white p-4">Your browser does not support the video tag. <a href="/banner-globe.mp4" className="text-red-500 underline">Click here to download the video</a>.</p>
            </video>
          </div>
        </div>
      </section>
    </div>
  );
};

function App() {
  return (
    <div className="App min-h-screen flex flex-col bg-black text-white">
      <BrowserRouter>
        <Navigation />
        <main className="flex-grow">
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/programs" element={<ProgramsPage />} />
            <Route path="/get-involved" element={<GetInvolvedPage />} />
            <Route path="/quotes" element={<QuotesPage />} />
            <Route path="/news" element={<NewsPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/revenue" element={<RevenuePage />} />
            <Route path="/facilities" element={<FacilitiesPage />} />
            <Route path="/leadership" element={<LeadershipPage />} />
            <Route path="/investment" element={<InvestmentPage />} />
            <Route path="/imagine" element={<ImaginePage />} />
          </Routes>
        </main>
        <Footer />
      </BrowserRouter>
    </div>
  );
}

export default App;
